import { ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { buildClientTokenContext } from "@/contexts/clientAccessTokenContext";
import { usePackageSuggestionsNewQuery } from "@/graphql/queries/packageSuggestionsNew.graphql.types";
import { MedspaOnlineStoreData } from "@/views/online-store/onlineStoreView";

type UseGetPackageSuggestionsProps = {
  clientAccessToken: string;
  clientId: string;
  packagesInfo: MedspaOnlineStoreData["packages"];
  appointmentServiceIds?: string[];
  error?: string | ApolloError;
  loading?: boolean;
};

export function useGetAppointmentPackageSuggestions({
  clientAccessToken,
  clientId,
  packagesInfo,
  appointmentServiceIds,
  error,
  loading,
}: UseGetPackageSuggestionsProps) {
  const { data: packageSuggestionsData, loading: packageSuggestionsLoading } =
    usePackageSuggestionsNewQuery({
      variables: {
        clientId,
        appointmentServiceIds,
      },
      context: buildClientTokenContext(clientAccessToken),
      skip: !clientId || appointmentServiceIds.length === 0,
    });

  const suggestedPackageIds = useMemo(
    () =>
      packageSuggestionsData?.medspaPackageSuggestionsNew.suggestedPackages.map(
        (packageItem) => packageItem.id
      ) ?? [],
    [packageSuggestionsData]
  );

  const suggestedPackages = useMemo(
    () =>
      packagesInfo?.filter((packageItem) =>
        suggestedPackageIds.includes(packageItem.id)
      ) ?? [],
    [packagesInfo, suggestedPackageIds]
  );

  return useMemo(
    () => ({
      suggestedPackages,
      loading:
        !error &&
        (loading ||
          packageSuggestionsLoading ||
          packageSuggestionsData === undefined),
      error,
    }),
    [
      suggestedPackages,
      error,
      loading,
      packageSuggestionsLoading,
      packageSuggestionsData,
    ]
  );
}
